<template>
  <b-card
    id="checkin-status-card"
    class="border"
  >
    <!-- Status Progress Header -->
    <div class="checkin-progress-header mb-3">
      <h5 class="mb-1 text-center">{{ $t("pages.booking.checkin.progressBarTitle") }}</h5>
      <div class="progress-indicator-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="`width: ${completionPercentage}%`"
            :class="onlineCheckinCompleted ? 'bg-success' : 'bg-warning'"
          ></div>
        </div>
        <div class="progress-text text-center small mt-1">
          <span :class="onlineCheckinCompleted ? 'text-success' : 'text-warning'">
            {{ completionPercentage }}% {{ $t("pages.booking.checkin.progressBarCompleted") }}
          </span>
        </div>
      </div>
    </div>

    <!-- Status Steps -->
    <div class="checkin-steps-container">
      <!-- Client Step -->
      <div class="checkin-step" :class="{'completed': clientOnlineCheckinCompleted, 'active': !clientOnlineCheckinCompleted && !guestsOnlineCheckinCompleted && !arrivalOnlineCheckinCompleted}">
        <b-img
          class="step-icon"
          src="@/assets/foravila/images/illustrations/add-user.svg"
        />
        <div class="step-content">
          <h6 class="step-title">{{ $t("Booking holder") }}</h6>
          <b-badge
            pill
            :variant="clientBadgeVariant"
            class="step-badge"
          >
            {{ clientBadgeText }}
          </b-badge>
        </div>
      </div>

      <!-- Connector -->
      <div class="step-connector" :class="clientOnlineCheckinCompleted ? 'completed' : ''"></div>
      
      <!-- Guests Step -->
      <div class="checkin-step" :class="{'completed': guestsOnlineCheckinCompleted, 'active': clientOnlineCheckinCompleted && !guestsOnlineCheckinCompleted}">
        <b-img
          class="step-icon"
          src="@/assets/foravila/images/illustrations/guests.svg"
        />
        <div class="step-content">
          <h6 class="step-title">{{ $t("Guests") }}</h6>
          <b-badge
            pill
            :variant="guestsBadgeVariant"
            class="step-badge"
          >
            {{ guestsBadgeText }}
          </b-badge>
        </div>
      </div>

      <!-- Connector -->
      <div class="step-connector" :class="guestsOnlineCheckinCompleted ? 'completed' : ''"></div>
      
      <!-- Schedules Step -->
      <div class="checkin-step" :class="{'completed': arrivalOnlineCheckinCompleted, 'active': guestsOnlineCheckinCompleted && !arrivalOnlineCheckinCompleted}">
        <b-img
          class="step-icon"
          src="@/assets/foravila/images/illustrations/arrival.svg"
        />
        <div class="step-content">
          <h6 class="step-title">{{ $t("Schedules") }}</h6>
          <b-badge
            pill
            :variant="arrivalBadgeVariant"
            class="step-badge"
          >
            {{ arrivalBadgeText }}
          </b-badge>
        </div>
      </div>
    </div>

    <!-- Status Message and Actions -->
    <div class="checkin-footer mt-5">
      
      <div v-if="onlineCheckinCompleted" class="action-buttons">
        <b-button v-b-modal.edit-checkin-modal variant="outline-primary" class="mr-2">
          {{ $t("Contact us") }}
        </b-button>
        <b-button
          variant="primary"
          @click="onArrivalInstructionsButtonClicked"
        >
          {{ $t("Arrival instructions") }}
        </b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BBadge,
  BImg,
  BButton,
  VBModal
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BBadge,
    BImg,
    BButton
  },
  directives: {
    "b-modal": VBModal
  },
  data() {
    return {
      INSTRUCTIONS_TAB_INDEX: 2
    };
  },
  computed: {
    guests() {
      return this.$store.getters["booking/guests"];
    },
    onlineCheckinCompleted() {
      return this.$store.getters["booking/onlineCheckinCompleted"];
    },
    clientOnlineCheckinCompleted() {
      return this.$store.getters["booking/clientOnlineCheckinCompleted"];
    },
    guestsOnlineCheckinCompleted() {
      return this.$store.getters["booking/guestsOnlineCheckinCompleted"];
    },
    arrivalOnlineCheckinCompleted() {
      return this.$store.getters["booking/arrivalOnlineCheckinCompleted"];
    },
    completionPercentage() {
      let completed = 0;
      if (this.clientOnlineCheckinCompleted) completed++;
      if (this.guestsOnlineCheckinCompleted) completed++;
      if (this.arrivalOnlineCheckinCompleted) completed++;
      
      return Math.round((completed / 3) * 100);
    },
    clientBadgeText() {
      return this.clientOnlineCheckinCompleted
        ? this.$t("Complete-m")
        : this.$t("Incomplete-m");
    },
    clientBadgeVariant() {
      return this.clientOnlineCheckinCompleted
        ? "light-success"
        : "light-warning";
    },
    guestsBadgeText() {
      if (!this.guests.length)
        return this.$t("pages.booking.checkin.noGuestsRegistered");
      return this.guestsOnlineCheckinCompleted
        ? this.$t("Complete-m")
        : this.$t("Incomplete-m");
    },
    guestsBadgeVariant() {
      return this.guestsOnlineCheckinCompleted
        ? "light-success"
        : "light-warning";
    },
    arrivalBadgeText() {
      return this.arrivalOnlineCheckinCompleted
        ? this.$t("Complete-m")
        : this.$t("Incomplete-m");
    },
    arrivalBadgeVariant() {
      return this.arrivalOnlineCheckinCompleted
        ? "light-success"
        : "light-warning";
    },
  },
  methods: {
    onArrivalInstructionsButtonClicked() {
      this.$store.dispatch("app/setBookingTab", this.INSTRUCTIONS_TAB_INDEX);
    }
  }
};
</script>

<style lang="scss">
@import "~@core/scss/base/bootstrap-extended/include";

#checkin-status-card {
  // Progress bar styles
  .progress-indicator-container {
    margin: 0.5rem auto;
    max-width: 300px;
    
    .progress-bar {
      height: 8px;
      background-color: $gray-200;
      border-radius: 4px;
      overflow: hidden;
      
      .progress-fill {
        height: 100%;
        transition: width 0.5s ease;
      }
    }
  }
  
  // Steps container
  .checkin-steps-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    
    @include media-breakpoint-up(md) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }
  
  // Step styles
  .checkin-step {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid $gray-200;
    transition: all 0.3s ease;
    
    &.completed {
      border-color: $success;
      background-color: rgba($success, 0.05);
    }
    
    &.active {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba($primary, 0.2);
    }
    
    @include media-breakpoint-up(md) {
      flex: 1;
      flex-direction: column;
      text-align: center;
      margin-bottom: 0;
      position: relative;
      padding: 1rem 0.5rem;
    }
    
    // Step icon (moved from step-icon-container)
    .step-icon {
      width: 70px;
      height: 70px;
      margin-right: 1rem;
      
      @include media-breakpoint-up(md) {
        width: 150px;
        height: 150px;
        margin: 0 auto 1rem;
      }
      
      &.bg-success,
      &.bg-warning {
        // These classes don't need background colors on the image itself
        background-color: transparent;
      }
    }
  }
  
  // Step content
  .step-content {
    flex: 1;
    
    .step-title {
      margin-bottom: 0.25rem;
      font-weight: 600;
    }
    
    .step-badge {
      font-size: 0.7rem;
    }
  }
  
  // Step connector
  .step-connector {
    display: none;
    
    @include media-breakpoint-up(md) {
      display: block;
      flex: 0 0 30px;
      height: 3px;
      background-color: $gray-300;
      
      &.completed {
        background-color: $success;
      }
    }
  }
  
  // Footer section
  .checkin-footer {
    .action-buttons {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
  }
}
</style>
